import { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTypedParams } from 'react-router-typesafe-routes/dom';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page/app-page';
import {
  NotificationsProvider,
  NotificationsRender,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { appRoute } from 'app/railways/router/routes';
import { Loading, PageContent, Stack } from 'app/ui';

import { CanAccess } from '../../access/features/permissions';
import { TReportTemplate } from '../core/report';
import { DetailsReportTemplate } from '../features/details-reports/details-report-template';
import { ReportView } from '../features/report-view/report-view';
import { getReportTemplateFileRequest } from '../requests/get-report-template-file.request';
import { getReportTemplateRequest } from '../requests/get-report-template.request';
import { updateReportTemplateRequest } from '../requests/update-report-template.request';

import styles from './details-reports.page.module.css';

type FormFields = { name: string; file?: Blob };

type UpdatedReportTemplate = FormFields & Pick<TReportTemplate, 'id'>;

export const DetailsReportsPage = observer(() => {
  const { id } = useTypedParams(appRoute.REPORT);

  const pageNotify = useNotifyFactory();

  const [templateFile, setTemplateFile] = useState<Blob | undefined>(undefined);

  const getReportFileResource = useResource<Pick<TReportTemplate, 'id'>, Blob>({
    request: getReportTemplateFileRequest,
    errorNotify: pageNotify,
    onDone: (tplFile) => {
      setTemplateFile(tplFile);
    },
  });
  const [reportData, setReportData] = useState<TReportTemplate>({
    id: Number(id),
    name: '',
    fileName: '',
  });

  const getReportTemplateResource = useResource<
    Pick<TReportTemplate, 'id'>,
    TReportTemplate
  >({
    request: getReportTemplateRequest,
    errorNotify: pageNotify,
    onDone: (reportData) => {
      setReportData(reportData);
    },
  });

  const updateReportTemplateResource = useResource<
    UpdatedReportTemplate,
    TReportTemplate
  >({
    request: updateReportTemplateRequest,
    errorNotify: pageNotify,
    onDone: (orderData) => {
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные успешно обновлены.',
      });

      setReportData(orderData);
    },
  });

  const handleUpdatedTemplate = (fields: FormFields) => {
    updateReportTemplateResource.send({ ...fields, id: id || 0 });
  };

  useEffect(() => {
    if (!id) return;

    getReportFileResource.send({ id });
    getReportTemplateResource.send({ id });
  }, [getReportFileResource, getReportTemplateResource, id]);

  const isReportLoading = getReportFileResource.isLoading;

  const isReportTemplateLoading =
    updateReportTemplateResource.isLoading ||
    getReportTemplateResource.isLoading;

  return (
    <div className={styles.root}>
      <NotificationsProvider value={pageNotify}>
        <AppPage perm="PERM_REPORT_TEMPLATE_READ" style={{ height: '100%' }}>
          <Tabs
            style={{ marginTop: 1, height: '100%' }}
            tabBarStyle={{
              margin: 0,
              background: 'white',
              padding: '0 16px',
            }}
          >
            <Tabs.TabPane
              tab="Просмотр отчета"
              key="report-editor"
              style={{ height: '100%' }}
              forceRender
            >
              <CanAccess
                perm="PERM_REPORT_TEMPLATE_READ"
                fallback="У вас нет доступа к этой функции"
              >
                <PageContent style={{ height: '100%' }}>
                  <Loading active={isReportLoading}>
                    <ReportView reportTemplate={templateFile} />
                  </Loading>
                </PageContent>
              </CanAccess>
            </Tabs.TabPane>

            <Tabs.TabPane
              tab="Параметры отчета"
              key="report-params"
              forceRender
            >
              <CanAccess
                perm="PERM_REPORT_TEMPLATE_UPDATE"
                fallback="У вас нет доступа к этой функции"
              >
                <PageContent>
                  <Stack>
                    <NotificationsRender notifyStore={pageNotify} />

                    <Loading active={isReportTemplateLoading}>
                      <DetailsReportTemplate
                        reportTemplate={reportData}
                        onUpdated={handleUpdatedTemplate}
                      />
                    </Loading>
                  </Stack>
                </PageContent>
              </CanAccess>
            </Tabs.TabPane>
          </Tabs>
        </AppPage>
      </NotificationsProvider>
    </div>
  );
});
